/** Right-side drawer with one invite: the summary, its context (each key interpreted or not),
 *  the claim, the tenant created from it, and the timeline (plan §15 "Detail drawer", §11).
 *
 *  The drawer never shows the link: Copy link / QR code in its Actions menu fetch it (audited).
 *  The actions run through the page's runner, whose dialogs are the drawer's siblings. */
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as invitesApi from '@/api/onboardingInvites';
import type { InviteDetail } from '@/api/onboardingInvites';
import { serialize } from '@/components/QuerySearch';
import { TsValue } from '@/features/bgtasks/taskFormat';
import { usePermissions } from '@/hooks/usePermissions';
import { auditHref } from '@/utils/gridLinks';

import { InviteActionsMenu, type RunInviteAction } from './InviteActions';
import { tenantOnboardingPath, type InviteActionPerms } from './inviteActionRules';
import { InviteStatusChip, inviteMarketplaceText, inviteSourceLabel, inviteStatusHint, userLabel } from './inviteFormat';
import { InviteTimeline } from './InviteTimeline';

const mono = { fontFamily: 'monospace', fontSize: 12, overflowWrap: 'anywhere' } as const;

interface Props {
  inviteId: number;
  open: boolean;
  onClose: () => void;
  perms: InviteActionPerms;
  onRun: RunInviteAction;
}

/** `/onboarding-invites?q=batch=<id>` — the batch's invites in the grid. */
export const batchHref = (batchId: string) =>
  `/onboarding-invites?${new URLSearchParams({
    q: serialize([{ kind: 'cond', namespace: 'batch', operator: 'exact', value: batchId }]),
  }).toString()}`;

export function InviteDetailDrawer({ inviteId, open, onClose, perms, onRun }: Props) {
  const { hasPerm } = usePermissions();
  const query = useQuery({
    queryKey: invitesApi.invitesKeys.detail(inviteId),
    queryFn: () => invitesApi.retrieve(inviteId),
    enabled: open,
  });
  const invite = query.data;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // The AppBar sits at zIndex.drawer+1; without this the app bar covers the drawer top.
      sx={{ zIndex: (t) => t.zIndex.modal }}
    >
      <Box sx={{ width: { xs: '100vw', sm: 560 }, maxWidth: '100vw', p: 2 }} role="presentation">
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
            Invite #{inviteId}
          </Typography>
          {invite && <InviteStatusChip status={invite.status} />}
          {invite && <InviteActionsMenu invite={invite} perms={perms} onRun={onRun} variant="button" />}
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>

        {query.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {query.isError && <Alert severity="error">Couldn’t load the invite.</Alert>}

        {invite && (
          <Stack spacing={2}>
            <Summary invite={invite} />
            <Divider />
            <ContextSection invite={invite} />
            <Divider />
            <ClaimSection invite={invite} />
            {(invite.tenant || invite.used_ts) && (
              <>
                <Divider />
                <TenantSection invite={invite} canOpenTenant={perms.canOpenTenant} canOpenUsers={hasPerm('console_user_view')} />
              </>
            )}
            <Divider />
            <Stack direction="row" sx={{ alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                Timeline
              </Typography>
              {hasPerm('console_audit_view_all') && (
                <Link component={RouterLink} to={auditHref({ invite: String(inviteId) })} variant="body2">
                  Open in the audit log
                </Link>
              )}
            </Stack>
            <InviteTimeline inviteId={inviteId} />
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}

function Rows({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <Table size="small" sx={{ '& td': { px: 0.5, py: 0.5, borderBottom: 'none', verticalAlign: 'top' } }}>
      <TableBody>
        {rows.map(([label, value]) => (
          <TableRow key={label}>
            <TableCell component="th" scope="row" sx={{ width: 150, color: 'text.secondary', fontSize: 13 }}>
              {label}
            </TableCell>
            <TableCell sx={{ fontSize: 13 }}>{value}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const dash = <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>;

function Summary({ invite }: { invite: InviteDetail }) {
  const rows: [string, ReactNode][] = [
    ['Label', invite.label],
    ['Status', inviteStatusHint(invite.status)],
    ['Public id', <Box component="span" sx={mono} key="pid">{invite.public_id}</Box>],
    [
      'E-mail',
      invite.email ? (
        <span>
          {invite.email} <Typography component="span" variant="caption" color="text.secondary">(bound)</Typography>
        </span>
      ) : (
        <span>
          Unbound — the client verifies the e-mail they type
          {invite.verified_email ? (
            <>
              {' '}
              (verified: <strong>{invite.verified_email}</strong>)
            </>
          ) : null}
        </span>
      ),
    ],
    [
      'Created',
      <span key="created">
        <TsValue value={invite.created_ts} /> ·{' '}
        {invite.created_by || invite.source !== 'marketplace' ? userLabel(invite.created_by) : 'the marketplace install'}
      </span>,
    ],
    ['Expires', invite.expires_ts ? <TsValue value={invite.expires_ts} /> : 'Never'],
    [
      'E-mails sent',
      invite.send_count > 0 ? (
        <span key="sent">
          {invite.send_count} · last <TsValue value={invite.sent_ts} />
        </span>
      ) : (
        'None'
      ),
    ],
    ['Code version', String(invite.code_version)],
    ['Source', inviteSourceLabel(invite)],
  ];
  const install = invite.source === 'marketplace' ? inviteMarketplaceText(invite.context) : null;
  if (install) rows.push(['Installed by', install]);
  if (invite.batch_id) {
    rows.push([
      'Batch',
      <Link component={RouterLink} to={batchHref(invite.batch_id)} sx={mono} key="batch">
        {invite.batch_id}
      </Link>,
    ]);
  }
  if (invite.revoked_ts) {
    rows.push([
      'Revoked',
      <span key="revoked">
        <TsValue value={invite.revoked_ts} /> · {userLabel(invite.revoked_by)}
        {invite.revoke_reason ? ` — “${invite.revoke_reason}”` : ''}
      </span>,
    ]);
  }
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Summary
      </Typography>
      <Rows rows={rows} />
    </Box>
  );
}

function ContextSection({ invite }: { invite: InviteDetail }) {
  const ctx = invite.context ?? {};
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Context
      </Typography>
      {invite.context_keys.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No context: every booking system is offered and the tenant code is derived.
        </Typography>
      ) : (
        <Table size="small" sx={{ '& td': { px: 0.5, py: 0.5, verticalAlign: 'top' } }}>
          <TableBody>
            {invite.context_keys.map((k) => (
              <TableRow key={k.key} data-context-key={k.key}>
                <TableCell sx={{ width: 150 }}>
                  <Box component="code" sx={mono}>
                    {k.key}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box component="code" sx={mono}>
                    {JSON.stringify(ctx[k.key])}
                  </Box>
                  {k.description && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      {k.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ width: 120 }} align="right">
                  {k.interpreted ? (
                    <Chip size="small" variant="outlined" color="info" label="interpreted" sx={{ height: 20 }} />
                  ) : (
                    <Chip size="small" variant="outlined" color="warning" label="not interpreted" sx={{ height: 20 }} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

function ClaimSection({ invite }: { invite: InviteDetail }) {
  const c = invite.claim;
  const draft = invite.draft ?? {};
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Claim
      </Typography>
      {c.claimed ? (
        <Rows
          rows={[
            ['Claimed since', <TsValue value={c.claimed_ts} key="since" />],
            ['Last seen', c.seen_ts ? <TsValue value={c.seen_ts} /> : dash],
            ['IP', c.ip ? <Box component="span" sx={mono}>{c.ip}</Box> : dash],
            ['Browser', c.user_agent ? <Box component="span" sx={{ fontSize: 12 }}>{c.user_agent}</Box> : dash],
          ]}
        />
      ) : (
        <Typography variant="body2" color="text.secondary">
          {invite.used_ts ? 'The link was used; it no longer has a holder.' : 'No browser holds the link.'}
        </Typography>
      )}
      {!invite.bound && invite.email_code_attempts > 0 && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          {invite.email_code_attempts} e-mail code attempt{invite.email_code_attempts === 1 ? '' : 's'}.
        </Typography>
      )}
      {Object.keys(draft).length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            The holder’s choices so far (never credentials; cleared on release)
          </Typography>
          <Box
            component="pre"
            sx={{ ...mono, m: 0, mt: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}
          >
            {JSON.stringify(draft, null, 2)}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function TenantSection({
  invite,
  canOpenTenant,
  canOpenUsers,
}: {
  invite: InviteDetail;
  canOpenTenant: boolean;
  canOpenUsers: boolean;
}) {
  const t = invite.tenant;
  const rows: [string, ReactNode][] = [
    [
      'Tenant',
      t ? (
        canOpenTenant ? (
          <Link component={RouterLink} to={`/tour-operators/${t.id}`}>
            {t.name} ({t.code})
          </Link>
        ) : (
          `${t.name} (${t.code})`
        )
      ) : (
        dash
      ),
    ],
  ];
  if (invite.tenant_code && invite.tenant_code !== t?.code) rows.push(['Code as created', invite.tenant_code]);
  rows.push([
    'Admin',
    invite.admin_user ? (
      canOpenUsers ? (
        <Link component={RouterLink} to={`/users/${invite.admin_user.id}`}>
          {userLabel(invite.admin_user)}
        </Link>
      ) : (
        userLabel(invite.admin_user)
      )
    ) : (
      dash
    ),
  ]);
  rows.push(['Created', invite.used_ts ? <TsValue value={invite.used_ts} /> : dash]);
  rows.push(['Went live', invite.completed_ts ? <TsValue value={invite.completed_ts} /> : 'Not yet']);
  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: 'center', mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          Tenant
        </Typography>
        {t && canOpenTenant && (
          <Link component={RouterLink} to={tenantOnboardingPath(t.id)} variant="body2">
            Open onboarding page
          </Link>
        )}
      </Stack>
      <Rows rows={rows} />
    </Box>
  );
}
